import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useContractContext } from "@/contexts/ContractContext";
import { isFluxPayContract } from "@/utils/contract";

import {
  isSystemDefaultAutomation,
  compareAutomationDisplayOrder,
  type SystemDefaultAutomationId,
} from "../constants/automationUiConstants";
import { useHasSectionOpened } from "../../management/hooks/useHasSectionOpened";
import { useAutomationService } from "../hooks/useAutomationService";
import type { AutomationContext, AutomationId } from "../types";
import AutomationDialog from "./AutomationDialog";
import AutomationsPanel from "./AutomationsPanel";
import DisableAutomationDialog from "./DisableAutomationDialog";

export interface AutomationsPanelSectionProps {
  context: AutomationContext;
  hasActiveMetaEndpoint?: boolean;
  isEndpointStatusLoading?: boolean;
  onNavigateToDefaultTemplates?: (
    automationId: SystemDefaultAutomationId,
  ) => void;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  dragHandleProps?: {
    attributes?: object;
    listeners?: object;
  };
  hasConfiguredCustomAutomations?: boolean;
  /** Incrementa após troca de canal para recarregar lista de automações. */
  automationRefreshSignal?: number;
}

const AutomationsPanelSection = memo(
  ({
    context,
    hasActiveMetaEndpoint = false,
    isEndpointStatusLoading = false,
    onNavigateToDefaultTemplates,
    isCollapsed = false,
    onToggleCollapsed,
    dragHandleProps,
    hasConfiguredCustomAutomations = false,
    automationRefreshSignal = 0,
  }: AutomationsPanelSectionProps) => {
    const [selectedAutomationIdForEdit, setSelectedAutomationIdForEdit] =
      useState<AutomationId | null>(null);
    const [automationIdPendingDisable, setAutomationIdPendingDisable] =
      useState<AutomationId | null>(null);

    const { currentContract } = useContractContext();
    const hasOpened = useHasSectionOpened(isCollapsed);
    const { automations, disableAutomation, refresh, isLoading } =
      useAutomationService(context, { enabled: hasOpened });

    // Critério FluxPay: contrato ativo do tipo fluxPayPlan via ContractContext
    // (mesma fonte usada em outras telas que leem currentContract).
    const hasFluxPayActive = isFluxPayContract(
      currentContract?.[0]?.resource,
    );

    const canEditDefaultTemplates =
      !isEndpointStatusLoading && hasActiveMetaEndpoint;

    const visibleAutomations = useMemo(() => {
      return automations
        .filter((automation) => {
          if (automation.id === "payment" && !hasFluxPayActive) {
            return false;
          }

          return true;
        })
        .sort((itemA, itemB) =>
          compareAutomationDisplayOrder(itemA.id, itemB.id),
        );
    }, [automations, hasFluxPayActive]);

    const handleOpenAutomationConfig = useCallback(
      (params: { automationId: AutomationId }) => {
        setSelectedAutomationIdForEdit(params.automationId);
      },
      [],
    );

    const handleCloseAutomationConfig = useCallback(() => {
      setSelectedAutomationIdForEdit(null);
    }, []);

    const handleOpenDisableAutomationDialog = useCallback(
      (params: { automationId: AutomationId }) => {
        if (isSystemDefaultAutomation(params.automationId)) return;

        setAutomationIdPendingDisable(params.automationId);
      },
      [],
    );

    const handleCloseDisableAutomationDialog = useCallback(() => {
      setAutomationIdPendingDisable(null);
    }, []);

    const handleConfirmDisableAutomation = useCallback(async () => {
      if (!automationIdPendingDisable) return;
      if (isSystemDefaultAutomation(automationIdPendingDisable)) return;

      await disableAutomation({ automationId: automationIdPendingDisable });
      setAutomationIdPendingDisable(null);
    }, [automationIdPendingDisable, disableAutomation]);

    const handleAfterAutomationConfigSaved = useCallback(async () => {
      await refresh();
    }, [refresh]);

    useEffect(() => {
      if (automationRefreshSignal <= 0) return;
      void refresh();
    }, [automationRefreshSignal, refresh]);

    return (
      <>
        <AutomationsPanel
          automations={visibleAutomations}
          organizationId={context.organizationId}
          canEditDefaultTemplates={canEditDefaultTemplates}
          onNavigateToDefaultTemplates={onNavigateToDefaultTemplates}
          isCollapsed={isCollapsed}
          onToggleCollapsed={onToggleCollapsed}
          dragHandleProps={dragHandleProps}
          onActivateAutomation={(automationId) => {
            handleOpenAutomationConfig({ automationId });
          }}
          onEditAutomation={(automationId) => {
            handleOpenAutomationConfig({ automationId });
          }}
          onDisableAutomation={(automationId) => {
            handleOpenDisableAutomationDialog({ automationId });
          }}
          hasConfiguredCustomAutomations={hasConfiguredCustomAutomations}
          onRefresh={() => {
            void refresh();
          }}
          isRefreshing={isLoading}
        />

        {selectedAutomationIdForEdit && (
          <AutomationDialog
            automationId={selectedAutomationIdForEdit}
            context={context}
            open={Boolean(selectedAutomationIdForEdit)}
            onClose={handleCloseAutomationConfig}
            onAfterSaved={handleAfterAutomationConfigSaved}
          />
        )}

        {automationIdPendingDisable && (
          <DisableAutomationDialog
            open={Boolean(automationIdPendingDisable)}
            onClose={handleCloseDisableAutomationDialog}
            onConfirm={handleConfirmDisableAutomation}
          />
        )}
      </>
    );
  },
);

AutomationsPanelSection.displayName = "AutomationsPanelSection";

export default AutomationsPanelSection;
